import {
  CommonHelperFunctions,
  DashboardState,
  EProjectType,
  ESdChangeReason,
  IAction,
  ICodeGenerationContext,
  IConditionalStyle,
  IDisplayItem,
  IKuikaStyle,
  InvisibleStylePart,
  IScreenDesign,
  SourceCodeGenerateTypes,
  SpecialDIIds,
  StyleHelper,
  StyleState,
  ViewPart,
  Visibility
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import classNames from "classnames";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithFormProp, WithForm } from "../../../../shared/hoc/with-form";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { projectGeneratesReactNative } from "../../app-settings/react-native-feature-gate";
import RightPanelScrollEnd from "../right-panel-scroll-end";
import ResetChangesPopover from "./reset-changes-popover/reset-changes-popover";
import { BorderComponent } from "./styling-border-component";
import ConditionalStylingComponent, { type ConditionalStylesUpdater } from "./styling-conditional-component";
import EffectsComponent from "./styling-effects-component";
import "./styling-panel.scss";
import { FillComponent } from "./styling-fill-component";
import { stylingPanelModeStore } from "./styling-panel-mode-store";
import { PositionComponent } from "./styling-position-component";
import { ShadowComponent } from "./styling-shadow-component";
import { TypographyComponent } from "./styling-typography-component";
import {
  BASE_RESET_EXCLUDE_FIELDS,
  addEmptyBaseForSectionsCreatedInState,
  buildRestoredStyleObj,
  buildSerializableScreenDesign,
  carryStyleTagIds,
  healInheritedStateStyles,
  type IEditDiBackup,
  type IFormReloadTarget,
  type IStyleTagCollections,
  loadConditionalStyleIntoFormData,
  preserveDiOwnedStyleProps,
  preserveStateStylesFromSource,
  resolveSectionLoadMode,
  resolveFormReloadTarget,
  resolveSelectionChangeRestore,
  resolveStyleFormSync,
  resolveStateSwitchFormSeed,
  saveFormDataToConditionalStyle,
  saveFormDataToStateStyles,
  STYLE_SECTIONS,
  type StyleFormSyncAction
} from "./styling-window-helpers";
import { UnifiedStylingSelector } from "./unified-styling-selector/unified-styling-selector";

export interface IStylingWindowState {
  isEditMode: boolean;
  editingGroupId: string | null;
  /** KOA-18159: tag being edited (unified groupId, or a legacy tag's section id). */
  editingTagId: string | null;
  selectedState: StyleState;
  statePreviewBackup: IKuikaStyle | null;
  pendingTagStateStyles: IKuikaStyle | null;
  /** KOA-15838: pre-edit DI styleObj, so tag-edit preview leaves no inline residue. */
  editDiBackup: IEditDiBackup | null;
  /**
   * KOA-9069: the conditional style the styling form is currently bound to, or null for the
   * base/state target. Deliberately NOT folded into `selectedState`: states are a closed set
   * of four hard-coded across ~30 enumeration sites, conditions are N-ary and user-defined.
   */
  selectedConditionalStyleId: string | null;
}

export interface IStylingWindowProps {
  updateDebounce: number;
}

class StylingWindow extends PureSdComponent<
  IStylingWindowProps & IWithSDProps & IWithFormProp<IKuikaStyle>,
  IStylingWindowState
> {
  private isStateTransitioning = false;

  // One-shot flag set by handleResetState right before it auto-switches state
  // after resetting a tag's state overlay. handleStateChange normally flushes
  // the OUTGOING state's current form values into pendingTagStateStyles (to
  // capture a last unsaved edit before leaving). After a reset that form still
  // holds the just-reset values, so the flush would re-accumulate them into
  // pending and saveUnifiedStyle would write them back — silently undoing the
  // reset. When set, the next handleStateChange skips that flush. Consumed
  // (reset) at the top of handleStateChange so it can never poison a later,
  // unrelated state switch.
  private suppressPendingFlushOnce = false;

  // One-shot flag set by the Effects Presets flow right before it submits a
  // state-targeted preset (hover/pressed/focused/disabled). When set, the next
  // state-mode submit routes the preset ONLY into the state bucket and skips the
  // base-preview write — otherwise the preset leaks onto the base/none state and
  // shows at rest as well as in the target state. Consumed (reset) on the next
  // handleStyleFormFinish so it never affects a later edit.
  private suppressBasePreviewOnce = false;

  /** Whether `waitAndFormSubmit` has a submit waiting for its timer. */
  private hasPendingSubmit = false;

  // KOA-9069: one-shot routing override set immediately before the form.submit() that
  // handleStateChange issues. That submit's values belong to the BASE/state style, but
  // antd resolves onFinish in a microtask — long enough for a setState queued by the
  // caller (handleSelectedConditionalStyleChanged) to have already pointed
  // `selectedConditionalStyleId` at a condition, which would send the base values into
  // that condition instead. Captured synchronously here, consumed in handleStyleFormFinish.
  private forceBaseStyleTargetOnce: boolean = false;

  // Guards the deferred conditional-style selection (see handleSelectedConditionalStyleChanged)
  // against running after the panel has gone away.
  private isUnmounted: boolean = false;

  constructor(props: IStylingWindowProps & IWithSDProps & IWithFormProp<IKuikaStyle>) {
    super(props);
    this.state = {
      isEditMode: false,
      editingGroupId: null,
      editingTagId: null,
      selectedState: "none",
      statePreviewBackup: null,
      pendingTagStateStyles: null,
      editDiBackup: null,
      selectedConditionalStyleId: null
    };
  }

  componentDidMount() {
    // Sanitize the screen design before it is serialized for persistence /
    // codegen so the state-mode preview mirror never leaks into the base.
    this.props.sd.registerSerializeCleaner(this.cleanScreenDesignForSerialize);
    this.loadStyleForm();
    this.publishMode();
    stylingPanelModeStore.setPendingEditFlusher(this.pendingEditFlusher);
  }

  /**
   * Tells writers outside the panel (the canvas selection bar) whether the panel is in a state, tag-edit
   * or condition mode — their writes would be lost to this panel's restore / commit then. The store
   * ignores an unchanged value, so calling this on every update costs nothing.
   */
  private publishMode(): void {
    stylingPanelModeStore.publish({
      kuikaId: this.props.sd.selectedKuikaIds[0] ?? null,
      selectedState: this.state.selectedState,
      isEditMode: this.state.isEditMode,
      isConditionMode: this.state.selectedConditionalStyleId !== null
    });
  }

  componentDidUpdate(prevProps: IStylingWindowProps & IWithSDProps & IWithFormProp<IKuikaStyle>) {
    const oldKuikaId = prevProps.sd.selectedKuikaIds[0];
    const newKuikaId = this.props.sd.selectedKuikaIds[0];
    if (oldKuikaId !== newKuikaId) {
      // Selection changed. This is the SINGLE owner of the teardown: it runs in
      // BOTH edit mode and per-DI state mode (the unified-styling-selector only
      // clears its own local edit state in its componentDidUpdate). Reading
      // `this.state.isEditMode` here is race-free because it was set in the
      // PRIOR commit (tag-edit entry) and nothing in this commit mutates it
      // before this runs — no dependency on child→parent setState ordering.
      this.cancelPendingSubmit();

      // Clear the form synchronously before any re-render runs. Without this
      // the styling panel briefly renders the new DI with the previous DI's
      // state-mode form values: every Form.Item that uses `shouldUpdate` (e.g.
      // the color swatch in styling-typography-component) calls
      // form.getFieldValue and reads the previous hover/pressed/focused color
      // until the setState callback below fires loadStyleForm. That stale read
      // is what surfaces as "I picked a new component and the panel still
      // shows the previous component's hover color even though its metadata
      // is clean."
      //
      // CRITICAL: resetFields fires onFieldsChange, and handleStyleFormFieldsChange
      // calls form.submit() synchronously for fontStyle / typoIds / fontSize-Style
      // / borderIds / fillIds / shadowId field paths. If that submit fires here
      // it runs against a partially-reset form while selectedState is still
      // "hover" and selectedKuikaIds is already the NEW DI — saveFormDataToStateStyles
      // then writes the leftover OLD-DI hover values into the NEW DI's hoverStyles
      // AND base. Gate the reset behind isStateTransitioning so the
      // onFieldsChange handler bails early.
      this.isStateTransitioning = true;
      this.props.form.resetFields();
      this.isStateTransitioning = false;

      // Undo the live preview the PREVIOUS DI carried. In edit mode this discards
      // the unsaved tag edit by restoring the clean pre-edit snapshot (editDiBackup,
      // keyed by its own kuikaId — correct even though selection already moved);
      // in per-DI state mode it rebuilds the previous DI's clean base from
      // statePreviewBackup. The tag's project entries are never mutated during a
      // tag edit, so the edit-mode case is a true discard. Single restore — no
      // double-commit, because the selector no longer fires onEditModeChange here.
      const prevDi = this.props.sd.screenDesignDictionary[parseInt(oldKuikaId, 10)];
      const restore: IEditDiBackup | null = resolveSelectionChangeRestore({
        isEditMode: this.state.isEditMode,
        editDiBackup: this.state.editDiBackup,
        selectedState: this.state.selectedState,
        statePreviewBackup: this.state.statePreviewBackup,
        prevStyleObj: prevDi?.styleObj,
        prevKuikaId: oldKuikaId
      });
      if (restore) {
        this.props.sd.applyPropertyGridChangesToDisplay(
          { propertyName: "styleObj", value: restore.styleObj, kuikaId: restore.kuikaId },
          ESdChangeReason.ChangeStyleValue
        );
      }

      this.setState(
        {
          selectedState: "none",
          statePreviewBackup: null,
          pendingTagStateStyles: null,
          isEditMode: false,
          editingGroupId: null,
          editingTagId: null,
          editDiBackup: null,
          // KOA-9069: a conditional style belongs to ONE component, so the selection cannot
          // survive a move to another. Leaving it set does more than leave the panel washed
          // violet: the form stays bound to an id that is not in the new component's list, so
          // saveFormDataToConditionalStyle finds no target and every style edit is silently
          // dropped. Cleared here with the rest of the per-DI edit state.
          selectedConditionalStyleId: null
        },
        () => {
          this.loadStyleForm();
        }
      );
    }
    if (
      this.props.sd.changeReason === ESdChangeReason.Undo ||
      this.props.sd.changeReason === ESdChangeReason.Redo ||
      this.props.sd.changeReason === ESdChangeReason.LoadProject ||
      this.props.sd.changeReason === ESdChangeReason.DragDropDi ||
      this.props.sd.changeReason === ESdChangeReason.DragDropBlock
    ) {
      this.loadStyleForm();
    }
    this.publishMode();
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    // Panel hidden mid-edit (right panel collapsed, dashboard switch, …) while
    // in a non-none state: the live DI base is still preview-corrupted and the
    // backup is about to be destroyed with this component. Commit a restored
    // styleObj so any later save persists the clean base. Same idiom as the
    // selection-change restore in componentDidUpdate (T7).
    const { selectedState, statePreviewBackup, isEditMode } = this.state;
    const kuikaId: string | undefined = this.props.sd.selectedKuikaIds[0];
    if (!isEditMode && selectedState !== "none" && statePreviewBackup && kuikaId) {
      const liveDi: IDisplayItem | undefined = this.props.sd.screenDesignDictionary[parseInt(kuikaId, 10)];
      if (liveDi?.styleObj) {
        const restoredStyleObj: IKuikaStyle = buildRestoredStyleObj(liveDi.styleObj, statePreviewBackup, true);
        // Only commit when the restore actually changes something — avoids a
        // spurious undo entry / dirty mark when the user entered state mode but
        // never edited anything before the panel unmounted.
        const hasChange: boolean = !_.isEqual(liveDi.styleObj, restoredStyleObj);
        if (hasChange) {
          this.props.sd.applyPropertyGridChangesToDisplay(
            { propertyName: "styleObj", value: restoredStyleObj, kuikaId },
            ESdChangeReason.ChangeStyleValue
          );
        }
      }
    }
    this.props.sd.unregisterSerializeCleaner(this.cleanScreenDesignForSerialize);
    this.cancelPendingSubmit();
    stylingPanelModeStore.clearPendingEditFlusher(this.pendingEditFlusher);
    stylingPanelModeStore.reset();
  }

  /**
   * Pull-model serialize-cleaner registered on the SD context (componentDidMount).
   * Called at every outbound serialization of the screen design (save / preview /
   * run). Reads the panel's CURRENT state at call time — no enter/exit sync — and
   * delegates to the stateless {@link buildSerializableScreenDesign}, which strips
   * the state-mode preview mirror from the selected DI's base on a clone without
   * touching the live tree or the canvas.
   */
  cleanScreenDesignForSerialize = (screenDesign: IScreenDesign): IScreenDesign => {
    return buildSerializableScreenDesign(
      screenDesign,
      this.state.selectedState,
      this.state.statePreviewBackup,
      this.props.sd.selectedKuikaIds[0],
      this.state.isEditMode
    );
  };

  collapseChanged = (keys) => {
    this.props.sd.setStylingActiveKeys(keys);
  };

  loadStyleForm = () => {
    if (!this.props.form) {
      return;
    }

    const sd = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);

    if (!selectedDi) {
      return;
    }

    // KOA-15838 root-cause fix: `setEmptyFieldsInStyleObj` and
    // `convertDiStyleToFormStyle` both MUTATE their input in place
    // (`kuikaStyle.typoId = null`, `kuikaStyle.typoIds = [...]`, and — when
    // `kuikaid` matches `projectTempStyles.tempStylingTypography.kuikaid` —
    // they write `color`, `fontSize`, `fontFamily`, etc. INTO the section
    // object). The input here is `selectedDi.styleObj`, i.e. the DI in the
    // dictionary. Mutating it pollutes the persisted model with form-only
    // shape (typoId→null, ids exploded) AND, more importantly, copies the
    // currently-cached temp style values onto whichever DI is being loaded.
    // Deep-clone first so the form transformation operates on a throwaway
    // tree and the DI's persisted styleObj stays untouched until a real
    // commit flows through applyPropertyGridChangesToDisplay.
    const styleObjSnapshot: IKuikaStyle = _.cloneDeep(selectedDi.styleObj!) as IKuikaStyle;
    const styleObj = StyleHelper.setEmptyFieldsInStyleObj(styleObjSnapshot);

    const kuikaId = this.props.sd.selectedKuikaIds[0];
    const context = this.createCodeGenerationContext(sd);

    const baseFormData: IKuikaStyle = StyleHelper.convertDiStyleToFormStyle(context, kuikaId, styleObj);
    const target: IFormReloadTarget = resolveFormReloadTarget(
      selectedDi.styleObj,
      baseFormData,
      this.state.selectedConditionalStyleId
    );
    this.resetAndSetFormValues(target.formData);
    if (target.conditionalStyleId !== this.state.selectedConditionalStyleId) {
      this.setState({ selectedConditionalStyleId: target.conditionalStyleId });
    }
  };

  private createCodeGenerationContext = (sd: IScreenDesign): ICodeGenerationContext => {
    const { screenDesignDictionary, project, projectTempStyles } = this.props.sd;
    const masterScreenDesign = this.props.sd.masterSd;
    const sdPublic = this.props.sd.getScreenPublic(sd.id);
    const allActions: IAction[] = CommonHelperFunctions.getAllActions(sd.displayItems);

    const findDisplayItem = (id: any) => sd.displayItems.find((x) => x.id === id);

    const sdHeader = findDisplayItem(SpecialDIIds.headerDI);
    const sdFooter = findDisplayItem(SpecialDIIds.footerDI);
    const sdPageHeader = findDisplayItem(SpecialDIIds.pageHeaderDI);
    const sdPageFooter = findDisplayItem(SpecialDIIds.pageFooterDI);
    const sdLayoutHeader = findDisplayItem(SpecialDIIds.layoutHeaderDI);
    const sdLayoutFooter = findDisplayItem(SpecialDIIds.layoutFooterDI);
    const sdLayoutLeft = findDisplayItem(SpecialDIIds.layoutLeftDI);
    const sdLayoutRight = findDisplayItem(SpecialDIIds.layoutRightDI);
    const sdRoot = findDisplayItem(SpecialDIIds.rootDI);

    return {
      projectTempStyles,
      project,
      generateType: SourceCodeGenerateTypes.render,
      changeReason: ESdChangeReason.ChangeStyleValue,
      allActions,
      sd,
      screenDesignDictionary,
      dashboardState: this.props.sd.dashboardState,
      sdHeader,
      sdFooter,
      sdPageHeader,
      sdPageFooter,
      sdLayoutHeader,
      sdLayoutFooter,
      sdLayoutLeft,
      sdLayoutRight,
      sdRoot,
      sdPublic: sdPublic || {},
      masterScreenDesign
    };
  };

  private resetAndSetFormValues = (formData: IKuikaStyle) => {
    // Suppress onFieldsChange-driven submits while we rewrite the form. Without
    // this, fontStyle/typoIds/fontSizeStyle/borderIds/fillIds/shadowId field
    // resets would each call form.submit synchronously and persist a
    // partially-rewritten styleObj against the currently-selected DI.
    this.isStateTransitioning = true;
    this.props.form.resetFields();
    this.props.form.setFieldsValue(formData);
    this.isStateTransitioning = false;
  };

  // Called by the Effects Presets flow immediately before it submits a
  // state-targeted preset, so the upcoming state-mode submit writes the preset
  // into the state bucket only (no base-preview write). See suppressBasePreviewOnce.
  markStatePresetCommit = (): void => {
    this.suppressBasePreviewOnce = true;
  };

  handleStateChange = (state: StyleState, unifiedStyleId?: string, seedBackupFromSource?: boolean) => {
    this.cancelPendingSubmit(); // Always cancel pending debounced submit
    const { form, sd } = this.props;
    const previousState = this.state.selectedState;
    const { isEditMode, editingGroupId, pendingTagStateStyles } = this.state;
    // KOA-18159: callers that know only the target state (Effects Presets) omit the
    // tag id; without this fallback we'd re-source from the DI and lose the tag's
    // identity from the form, mis-routing the next save.
    const editingTagId: string | undefined = unifiedStyleId ?? this.state.editingTagId ?? undefined;

    const project = sd.project;
    const selectedDi = sd.getSelectedDisplayItem(sd.getScreenDesign());

    // Consume the one-shot reset guard BEFORE any early return below, so a
    // reset-triggered switch can never leave the flag set to poison the next
    // real state change.
    const suppressPendingFlush: boolean = this.suppressPendingFlushOnce;
    this.suppressPendingFlushOnce = false;

    // In edit mode, flush current form data into pending before switching states.
    // Skipped after a reset (suppressPendingFlush) — the outgoing state's form
    // still holds the just-reset values and re-capturing them would undo the
    // reset on the next saveUnifiedStyle.
    let currentPending = pendingTagStateStyles;
    if (!suppressPendingFlush && isEditMode && editingGroupId && previousState !== "none") {
      currentPending = currentPending ? _.cloneDeep(currentPending) : ({} as IKuikaStyle);
      const prevStateProp = `${previousState}Styles`;
      saveFormDataToStateStyles(currentPending, form.getFieldsValue(), prevStateProp, false);
    }

    let sourceStyle: IKuikaStyle | undefined;

    if (this.state.editingGroupId && editingTagId) {
      // KOA-15838 root-cause fix: when entering edit mode against a style tag,
      // the source style is composed of LIVE project-level entries
      // (`project.stylingPosition[...]` etc.) — assigning those by reference
      // means subsequent mutation of `sourceStyle.text` (e.g. via the form
      // handlers) would write through to the shared project entry and corrupt
      // every DI that references that tag. Clone each entry independently.
      const editingGroupId = this.state.editingGroupId;
      const idLower = editingTagId.toLowerCase();
      if (editingGroupId === "unified") {
        // Unified tag: all six collections share one `groupId`. KOA-18159: effects
        // was missing here, so a state switch cleared the Effects section and the
        // tag's saved effects never loaded back into the panel.
        sourceStyle = {
          position: _.cloneDeep(project.stylingPosition?.find((s) => s.groupId === editingTagId)),
          text: _.cloneDeep(project.stylingTypography?.find((s) => s.groupId === editingTagId)),
          fill: _.cloneDeep(project.stylingFill?.find((s) => s.groupId === editingTagId)),
          border: _.cloneDeep(project.stylingBorder?.find((s) => s.groupId === editingTagId)),
          shadow: _.cloneDeep(project.stylingShadow?.find((s) => s.groupId === editingTagId)),
          effects: _.cloneDeep(project.stylingEffects?.find((s) => s.groupId === editingTagId)),
          styleGroupId: editingTagId,
          styleGroupName: project.stylingPosition?.find((s) => s.groupId === editingTagId)?.name
        } as IKuikaStyle;
      } else {
        // KOA-15838 legacy fix: a single-panel (legacy) tag has NO `groupId` — it
        // is identified by the editing panel (`editingGroupId`) plus the section
        // id (`unifiedStyleId`). Matching by `groupId` like the unified branch
        // returns undefined for EVERY section, which the state-switch loop then
        // treats as "clear" → the tag's SAVED hover/pressed/focused/disabled
        // overlays never load back into the form (they look "lost" on re-edit).
        // Source ONLY the edited section, by its own id field, so those overlays
        // load; the other four sections legitimately stay empty (and thus clear
        // on a state switch, which is correct for a single-panel tag).
        sourceStyle = {
          position:
            editingGroupId === "layout"
              ? _.cloneDeep(project.stylingPosition?.find((s) => s.positionId?.toLowerCase() === idLower))
              : undefined,
          text:
            editingGroupId === "text"
              ? _.cloneDeep(project.stylingTypography?.find((s) => s.typoId?.toLowerCase() === idLower))
              : undefined,
          fill:
            editingGroupId === "fill"
              ? _.cloneDeep(project.stylingFill?.find((s) => s.fillId?.toLowerCase() === idLower))
              : undefined,
          border:
            editingGroupId === "border"
              ? _.cloneDeep(project.stylingBorder?.find((s) => s.borderId?.toLowerCase() === idLower))
              : undefined,
          shadow:
            editingGroupId === "shadow"
              ? _.cloneDeep(project.stylingShadow?.find((s) => s.shadowId?.toLowerCase() === idLower))
              : undefined,
          effects:
            editingGroupId === "effects"
              ? _.cloneDeep(project.stylingEffects?.find((s) => s.effectsId?.toLowerCase() === idLower))
              : undefined,
          styleGroupId: editingTagId,
          styleGroupName: undefined
        } as IKuikaStyle;
      }
    } else if (selectedDi) {
      // KOA-15838 root-cause fix: previously `sourceStyle = selectedDi.styleObj`
      // gave us a LIVE reference to the DI's styleObj. The state-change flow
      // below reads sub-sections from `sourceStyle` and feeds them into
      // `form.setFieldsValue` — antd then keeps these as form-field state.
      // If anything in that path mutates the section objects (and the
      // `convertI*Form*ToI*` helpers do exactly that), the mutation hits the
      // DI directly. Clone so the form-side state is its own tree.
      sourceStyle = _.cloneDeep(selectedDi.styleObj);
    }

    if (!sourceStyle) return;

    let statePreviewBackup = this.state.statePreviewBackup;
    if (seedBackupFromSource && state !== "none") {
      // KOA-15838: edit-mode entered while a state tab was already active (see
      // handleEditModeChange). Seed the backup from the TAG's project entries
      // (`sourceStyle`), NOT the DI: the DI's base is still the stale pre-edit
      // state preview, and writing the tag base onto the DI first then re-reading
      // it is defeated by antd's async form.submit/onFinish (that write lands in a
      // microtask AFTER this synchronous capture). Sourcing the backup directly
      // removes the ordering dependency so the preview base reflects the tag.
      statePreviewBackup = _.cloneDeep(sourceStyle);
    } else if (previousState === "none" && state !== "none" && !statePreviewBackup) {
      // In edit mode, backup the DI's current styleObj (which has edited preview values)
      // rather than sourceStyle (project metadata), so restoration is correct
      statePreviewBackup =
        isEditMode && selectedDi?.styleObj ? _.cloneDeep(selectedDi.styleObj) : _.cloneDeep(sourceStyle);
    } else if (state === "none" && statePreviewBackup) {
      const backup = statePreviewBackup;
      if (!isEditMode) {
        addEmptyBaseForSectionsCreatedInState(backup, selectedDi?.styleObj);
        preserveStateStylesFromSource(backup, selectedDi?.styleObj);
      }
      sourceStyle = backup;
      statePreviewBackup = null;
    }

    const fieldsValue: IKuikaStyle = resolveStateSwitchFormSeed(
      this.state.selectedConditionalStyleId,
      selectedDi?.styleObj,
      form.getFieldsValue()
    );

    STYLE_SECTIONS.forEach((section) => {
      const srcSection = sourceStyle![section];
      // KOA-15838 leak fix: decide per-section how to populate the form. The old
      // `if (!srcSection) return` left `fieldsValue[section]` holding the
      // PREVIOUS state's values whenever `sourceStyle` could not supply the
      // section — so switching INTO a non-none state leaked the base/none values
      // into hover/pressed/focused/disabled. This bites when the edited tag maps
      // to only one section (a single-panel/legacy tag) or is a partial unified
      // tag missing some collections. `resolveSectionLoadMode` now returns
      // "clear" for that case (emit an empty section, ids preserved) and only
      // "skip" (keep current form value) for none + absent source.
      const loadMode = resolveSectionLoadMode(!!srcSection, state);
      if (loadMode === "skip") return;

      const {
        hoverStyles,
        pressedStyles,
        focusedStyles,
        disabledStyles,
        positionId,
        typoId,
        fillId,
        borderId,
        shadowId,
        effectsId,
        ...baseProps
      } = (srcSection || {}) as any;

      let loadedProps;
      if (loadMode === "loadState") {
        if (isEditMode && editingGroupId) {
          // In edit mode: prefer pending state styles, fall back to project metadata
          const pendingSection = currentPending?.[section] as any;
          const stateKey = `${state}Styles`;
          loadedProps = pendingSection?.[stateKey] || (srcSection as any)?.[stateKey] || {};
        } else {
          loadedProps = (srcSection as any)?.[`${state}Styles`] || {};
        }
      } else if (loadMode === "loadBase") {
        loadedProps = baseProps;
      } else {
        // "clear": section absent from sourceStyle while switching into a state →
        // emit an empty section so the previous (none) values do not leak.
        loadedProps = {};
      }

      const existingIds = fieldsValue[section]
        ? {
            positionIds: (fieldsValue[section] as any).positionIds,
            typoIds: (fieldsValue[section] as any).typoIds,
            fillIds: (fieldsValue[section] as any).fillIds,
            borderIds: (fieldsValue[section] as any).borderIds,
            shadowIds: (fieldsValue[section] as any).shadowIds,
            effectsIds: (fieldsValue[section] as any).effectsIds
          }
        : {};

      (fieldsValue as any)[section] = {
        ...loadedProps,
        ...existingIds,
        positionId: null,
        typoId: null,
        fillId: null,
        borderId: null,
        shadowId: null,
        effectsId: null
      };
    });

    // KOA-18159: a DI-sourced style carries no group info — don't let it blank the
    // edited tag's identity, which saveUnifiedStyle keys its write on.
    if (!isEditMode || sourceStyle.styleGroupId) {
      fieldsValue.styleGroupId = sourceStyle.styleGroupId;
      fieldsValue.styleGroupName = sourceStyle.styleGroupName;
    }

    // KOA-9069 QA (E4/C2/F1/F3): selecting a state LEAVES condition mode, mirroring how
    // entering condition mode leaves state mode. The form has exactly one write target, and
    // handleStyleFormFinish routes on this id alone — leaving it set meant a hover fill set
    // after picking a condition was written into that condition's base, replacing its style
    // and painting every matching row unconditionally.
    this.setState(
      {
        selectedState: state,
        statePreviewBackup,
        pendingTagStateStyles: currentPending,
        selectedConditionalStyleId: null
      },
      () => {
        this.isStateTransitioning = true;
        form.resetFields();
        form.setFieldsValue(fieldsValue);
        this.isStateTransitioning = false;
        this.forceBaseStyleTargetOnce = true;
        form.submit();
      }
    );
  };

  handleStyleFormFinish = async (formData: IKuikaStyle) => {
    // KOA-15838 perf: child color pickers (typography/fill/border/shadow) call
    // setFieldsValue + form.submit() in the same tick. setFieldsValue triggers
    // onFieldsChange → handleStyleFormFieldsChange → waitAndFormSubmit() which
    // schedules a debounced submit; form.submit() then fires the immediate one.
    // Both eventually run handleStyleFormFinish against the same formData,
    // doubling applyPropertyGridChangesToDisplay dispatches per gesture.
    // Cancel the pending debounce when an immediate submit has already reached
    // us — getFieldsValue already carries every queued change, so no data is
    // lost. Pure debounce paths hit cancel as a no-op since the debounced call
    // has already fired by the time we're inside this handler.
    this.cancelPendingSubmit();
    const { selectedState, statePreviewBackup, isEditMode, editingGroupId, pendingTagStateStyles } = this.state;
    // Consume the one-shot preset flag (reset unconditionally so it can never
    // leak into a later, unrelated submit).
    const suppressBasePreview: boolean = this.suppressBasePreviewOnce;
    this.suppressBasePreviewOnce = false;
    const kuikaId = this.props.sd.selectedKuikaIds[0];

    let styleObjToSave: IKuikaStyle = { ...formData };

    // KOA-9069: while a conditional style is selected the form edits THAT overlay. Handled
    // before the state branch and returning early, because the two are mutually exclusive
    // targets and a condition takes no base mirror — writing the form onto base would paint
    // every row with a style only the matching rows earn.
    // Consume the one-shot base-target override (reset unconditionally so it can never
    // leak into a later, unrelated submit).
    const forceBaseStyleTarget: boolean = this.forceBaseStyleTargetOnce;
    this.forceBaseStyleTargetOnce = false;

    const selectedConditionalStyleId: string | null = forceBaseStyleTarget
      ? null
      : this.state.selectedConditionalStyleId;
    if (selectedConditionalStyleId !== null) {
      const sd = this.props.sd.getScreenDesign();
      const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
      if (!selectedDi) return;

      const conditionalStyleObj: IKuikaStyle = _.cloneDeep(selectedDi.styleObj ?? {}) as IKuikaStyle;
      saveFormDataToConditionalStyle(conditionalStyleObj, formData, selectedConditionalStyleId);

      this.props.sd.applyPropertyGridChangesToDisplay(
        { propertyName: "styleObj", value: conditionalStyleObj, kuikaId },
        ESdChangeReason.ChangeStyleValue
      );
      return;
    }

    if (selectedState !== "none") {
      const stateProp = `${selectedState}Styles`;

      if (isEditMode && editingGroupId) {
        // In edit mode: accumulate state styles into pending
        const pending = pendingTagStateStyles ? _.cloneDeep(pendingTagStateStyles) : ({} as IKuikaStyle);
        saveFormDataToStateStyles(pending, formData, stateProp, false);
        this.setState({ pendingTagStateStyles: pending });
      }

      // Update DI for visual preview (both edit mode and non-edit mode)
      const sd = this.props.sd.getScreenDesign();
      const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
      if (!selectedDi) return;

      // KOA-15838 state→state transition fix: previously we cloned
      // selectedDi.styleObj directly, which carries the LEFTOVER base-preview
      // values written by the previous state's updateBaseForPreview=true pass
      // (e.g. hover wrote color/width into base for canvas preview). On a
      // state→state switch the new state's form arrives mostly empty, so
      // saveFormDataToStateStyles' Object.assign cannot clear those leftover
      // base values — they leak through into the persist write and the canvas
      // keeps rendering the previous state's preview. Rebuild base wholesale
      // from statePreviewBackup (the pre-state-mode snapshot) while preserving
      // the accumulated state sub-objects, mirroring the wholesale replacement
      // already done on the "none" exit path below.
      const currentStyleObj = statePreviewBackup
        ? buildRestoredStyleObj(selectedDi.styleObj!, statePreviewBackup, !isEditMode)
        : (_.cloneDeep(selectedDi.styleObj!) as IKuikaStyle);
      // Normal state-mode edits mirror the value onto base for live canvas
      // preview (restored on exit). A one-shot preset commit suppresses that
      // mirror so the preset lands ONLY in the state bucket — never on base.
      saveFormDataToStateStyles(currentStyleObj, formData, stateProp, !suppressBasePreview);

      styleObjToSave = currentStyleObj;
    } else {
      const sd = this.props.sd.getScreenDesign();
      const selectedDi = this.props.sd.getSelectedDisplayItem(sd);

      if (statePreviewBackup && selectedDi) {
        STYLE_SECTIONS.forEach((section) => {
          if (statePreviewBackup[section]) {
            const { hoverStyles, pressedStyles, focusedStyles, disabledStyles, ...baseProps } = statePreviewBackup[
              section
            ] as any;
            if (styleObjToSave[section]) {
              const { positionIds, typoIds, fillIds, borderIds, shadowIds, effectsIds } = styleObjToSave[
                section
              ] as any;
              (styleObjToSave as any)[section] = {
                ...baseProps,
                positionIds,
                typoIds,
                fillIds,
                borderIds,
                shadowIds,
                effectsIds
              };
            }
          }
        });

        preserveStateStylesFromSource(styleObjToSave, selectedDi.styleObj);

        this.setState({ statePreviewBackup: null });
      } else if (selectedDi) {
        preserveStateStylesFromSource(styleObjToSave, selectedDi.styleObj);
      }
    }

    // KOA-15838 heal: when NOT editing a tag, strip any inline {state}Styles prop
    // whose value equals the section's referenced tag value — the redundant copy
    // left by the edit-mode preview leak. Identical CURRENT render (the backend
    // merge yields the same CSS), and surgical (genuine overrides differing from
    // the tag survive). It is a deliberate DEDUP, not strictly lossless: an inline
    // value a user pinned that happens to equal the tag is indistinguishable from a
    // leaked one, so it is also dropped — meaning a later tag edit will then drag
    // that element along. We accept that (no way to tell the two apart) to clean
    // previously-leaked DIs the moment any state on them is committed.
    if (!isEditMode) {
      healInheritedStateStyles(styleObjToSave, this.props.sd.project as unknown as IStyleTagCollections);
    }

    // KOA-9069: the base ("none") write is built from form data, which never carries the
    // DI-owned properties — committing any base style would therefore erase every conditional
    // style on the component. Restored here rather than in that one branch so no future branch
    // can reintroduce the hole; it is a no-op for the branches that already clone the DI.
    const liveStyleObj: IKuikaStyle | undefined = this.props.sd.getSelectedDisplayItem(
      this.props.sd.getScreenDesign()
    )?.styleObj;
    preserveDiOwnedStyleProps(styleObjToSave, liveStyleObj);

    this.props.sd.applyPropertyGridChangesToDisplay(
      { propertyName: "styleObj", value: styleObjToSave, kuikaId },
      ESdChangeReason.ChangeStyleValue
    );
  };

  /**
   * KOA-9069: persists the conditional-style LIST (add / remove / condition edits). The styles
   * themselves travel the normal form path — see the redirect in `handleStyleFormFinish`.
   */
  handleConditionalStylesChanged = (
    updater: ConditionalStylesUpdater,
    changeReason?: ESdChangeReason | undefined
  ): void => {
    const kuikaId: string = this.props.sd.selectedKuikaIds[0];
    if (!kuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
    if (!selectedDi) return;

    const styleObjToSave: IKuikaStyle = _.cloneDeep(selectedDi.styleObj ?? {}) as IKuikaStyle;
    // Applied against a read taken HERE, not in the caller: two mutations batched into one
    // React tick would otherwise both start from the pre-batch list and the second would
    // discard the first.
    const conditionalStyles: IConditionalStyle[] = updater(selectedDi.styleObj?.conditionalStyles ?? []);
    // An empty list is dropped rather than persisted as `[]`, so removing the last condition
    // returns the screen to a shape byte-identical to one that never had the feature.
    if (conditionalStyles.length === 0) {
      delete styleObjToSave.conditionalStyles;
    } else {
      styleObjToSave.conditionalStyles = conditionalStyles;
    }

    this.props.sd.applyPropertyGridChangesToDisplay(
      { propertyName: "styleObj", value: styleObjToSave, kuikaId },
      changeReason ?? ESdChangeReason.ChangeStyleValue
    );
  };

  /**
   * KOA-9069: binds the styling form to a conditional style, or releases it back to base.
   *
   * Entering condition mode leaves state mode, because a single form cannot write to two
   * overlays at once — and state mode additionally mirrors onto base for canvas preview, which
   * a condition must never do (it is true for some rows and false for others). The exit is
   * symmetric: handleStateChange clears the selected condition, so neither mode can be entered
   * while the other still owns the form.
   */
  handleSelectedConditionalStyleChanged = (conditionalStyleId: string | null): void => {
    // A typed edit (width, font size) is only queued on the debounce. Flush it now so it is
    // written to the overlay that was being edited, instead of being cancelled and lost or —
    // worse — landing after the swap and being written to the incoming one.
    this.waitAndFormSubmit.flush();

    if (conditionalStyleId !== null && this.state.selectedState !== "none") {
      this.handleStateChange("none");
    }

    // Both the flush above and handleStateChange's exit call form.submit(), and antd resolves
    // a submit in a microtask, reading the form's values at THAT point rather than at the call.
    // Reloading the form before those land would hand the outgoing overlay's write the incoming
    // overlay's values — which is what QA saw as two conditions trading their colours, and as a
    // condition's colour appearing on the base style. A macrotask defer runs only once the whole
    // microtask queue has drained, so every submit writes the values and target it was issued with.
    setTimeout((): void => {
      if (this.isUnmounted) return;
      this.applyConditionalStyleSelection(conditionalStyleId);
    }, 0);
  };

  /**
   * KOA-9069: points the styling form at a conditional style, or back at the base style.
   * Only ever called once any in-flight submit for the previous target has settled.
   */
  applyConditionalStyleSelection = (conditionalStyleId: string | null): void => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);

    // Captured BEFORE resetFields: the style-tag ids (`position.positionIds` etc.) live only in
    // the form. The DI persists one `|`-separated `positionId` string that the DI→form converter
    // explodes on the way in, so neither branch below can recover them from `styleObj` — read
    // them off it and the component shows "No style" with every style section collapsed.
    const currentFormValue: IKuikaStyle = this.props.form.getFieldsValue();

    const formData: IKuikaStyle =
      conditionalStyleId === null
        ? ((_.cloneDeep(selectedDi?.styleObj) ?? {}) as IKuikaStyle)
        : loadConditionalStyleIntoFormData(selectedDi?.styleObj, conditionalStyleId, currentFormValue);

    carryStyleTagIds(formData, currentFormValue);

    // The form is shared by base, every state and every condition, so retargeting it must
    // REPLACE the values, not merge them — antd's setFieldsValue merges, which would leak the
    // previously-selected overlay's props into the new one and save them there (KOA-9069 QA).
    // resetFields() fires onFieldsChange, hence the same transition guard the state switch uses.
    //
    // This runs BEFORE the setState, not in its callback: the state change is what re-keys and
    // remounts the style sections, and they read the form store as they render. Loading after
    // that render meant every section mounted against the OUTGOING target's values and kept
    // showing them — the panel lagged exactly one selection behind, which is what QA saw as two
    // conditions trading their styles. Nothing here depends on the new state, so there is no
    // reason to wait for it.
    this.isStateTransitioning = true;
    this.props.form.resetFields();
    this.props.form.setFieldsValue(formData);
    this.isStateTransitioning = false;

    this.setState({ selectedConditionalStyleId: conditionalStyleId });
  };

  /**
   * KOA-9069: the selected condition's user-facing name. Positional, matching the section's own
   * "Condition {n}" labels — and matching the emitted CSS, where index IS the precedence.
   */
  getSelectedConditionalStyleLabel = (): string => {
    const { selectedConditionalStyleId } = this.state;
    if (selectedConditionalStyleId === null) return "";

    const sd = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
    const conditionalStyles: IConditionalStyle[] = selectedDi?.styleObj?.conditionalStyles ?? [];
    const index: number = conditionalStyles.findIndex(
      (entry: IConditionalStyle): boolean => entry.id === selectedConditionalStyleId
    );

    return index === -1 ? "this condition" : `Condition ${index + 1}`;
  };

  waitAndFormSubmit = _.debounce(() => {
    this.hasPendingSubmit = false;
    this.props.form.submit();
  }, this.props.updateDebounce);

  /** Schedules the debounced submit, remembering that one is pending (lodash's debounce cannot say). */
  private scheduleFormSubmit(): void {
    this.hasPendingSubmit = true;
    this.waitAndFormSubmit();
  }

  private cancelPendingSubmit(): void {
    this.hasPendingSubmit = false;
    this.waitAndFormSubmit.cancel();
  }

  /**
   * Commits a pending debounced edit synchronously (`stylingPanelModeStore.flushPendingEdit`), for a
   * writer outside the panel that is about to patch the same styleObj. Straight into
   * `handleStyleFormFinish` — `form.submit()` / the debounce's own `flush()` validate asynchronously, so
   * the edit would land AFTER the outside write and revert it. Only on the base style: in a state or
   * tag-edit mode outside writers are locked (`getOutsideStyleWriteLock`).
   */
  private flushPendingSubmit(): void {
    if (!this.hasPendingSubmit || this.state.selectedState !== "none" || this.state.isEditMode) {
      return;
    }
    this.cancelPendingSubmit();
    void this.handleStyleFormFinish(this.props.form.getFieldsValue());
  }

  /** Stable identity for the store's register / unregister. */
  private readonly pendingEditFlusher = (): void => this.flushPendingSubmit();

  isNumber = (val): boolean => {
    return /^[-]?\d+$/.test(val);
  };

  hasAnyValueInSection = (section: string): boolean => {
    const formValues = this.props.form.getFieldsValue();
    if (
      !formValues ||
      !formValues[section] ||
      formValues[section].positionId ||
      formValues[section].typoId ||
      formValues[section].shadowId ||
      formValues[section].borderId ||
      formValues[section].fillId ||
      formValues[section].effectsId
    )
      return false;

    const sectionValues = formValues[section];

    const excludeIds = {
      position: [
        "positionIds",
        "display",
        "name",
        "isPaddingLocked",
        "widthStyle",
        "heightStyle",
        "maxWidthStyle",
        "maxHeightStyle",
        "minWidthStyle",
        "minHeightStyle",
        "componentType"
      ],
      text: ["typoIds", "fontSizeStyle", "name", "componentType"],
      shadow: ["shadowIds", "isVisible", "name", "componentType"],
      border: ["borderIds", "borderStyle", "isBorderRadiusLocked", "isBorderWidthLocked", "name", "componentType"],
      fill: [
        "fillIds",
        "bgImagePosition",
        "bgImageSize",
        "repeatSwitch",
        "stretch",
        "name",
        "imageFit",
        "componentType"
      ],
      effects: ["effectsIds", "isVisible", "name", "componentType"]
    };

    const isNonEmpty = (value: any): boolean => {
      if (value === undefined || value === null || value === "" || value === "transparent") return false;

      if (Array.isArray(value)) {
        return value.length > 0 && value.some(isNonEmpty);
      }

      if (typeof value === "object") {
        return Object.keys(value).length > 0 && Object.values(value).some(isNonEmpty);
      }

      return true;
    };

    const keysToCheck = Object.keys(sectionValues).filter(
      (key) => !excludeIds[section] || !excludeIds[section].includes(key)
    );

    return keysToCheck.some((key) => isNonEmpty(sectionValues[key]));
  };

  resetSectionChanges = (section: string): void => {
    const formValues = this.props.form.getFieldsValue();
    const resetValues = _.cloneDeep(formValues);

    Object.keys(formValues[section] || {}).forEach((key) => {
      const excludeFields = {
        position: ["positionIds", "display"],
        text: ["typoIds", "fontSizeStyle"],
        shadow: ["shadowIds", "isVisible"],
        border: ["borderIds", "borderStyle", "isBorderRadiusLocked", "isBorderWidthLocked"],
        fill: ["fillIds", "bgImagePosition", "bgImageSize", "repeatSwitch", "stretch"],
        effects: ["effectsIds"]
      };

      if (!excludeFields[section].includes(key)) {
        resetValues[section][key] = undefined;
      }
    });

    this.props.form.resetFields();
    this.props.form.setFieldsValue(resetValues);

    this.scheduleFormSubmit();
  };

  handleStyleFormFieldsChange = async (changedFields: any[], allFields: any[]): Promise<void> => {
    if (this.isStateTransitioning) {
      return;
    }
    if (!changedFields || changedFields.length === 0) {
      return;
    }
    if (changedFields[0].name[0] === "position") {
      const fieldName = changedFields[0].name[1] as string;
      const { value } = changedFields[0];

      const dimensionFields = [
        "paddingTop",
        "paddingRight",
        "paddingBottom",
        "paddingLeft",
        "width",
        "height",
        "minWidth",
        "minHeight",
        "maxWidth",
        "maxHeight",
        "xAxis",
        "yAxis"
      ];

      if (dimensionFields.includes(fieldName) && value === "") {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        if (fieldsValue?.position) {
          fieldsValue.position[fieldName] = undefined;
          this.props.form.setFieldsValue(fieldsValue);
        }
      }

      if (fieldName.indexOf("margin") > -1 || fieldName.indexOf("padding") > -1) {
        if (value && value !== "" && this.isNumber(value) === false) {
          return;
        }
      }

      if (fieldName === "marginTop") {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        if (fieldsValue && fieldsValue.position && fieldsValue.position.isMarginLocked === true) {
          fieldsValue.position.marginRight = value;
          fieldsValue.position.marginBottom = value;
          fieldsValue.position.marginLeft = value;
          this.props.form.setFieldsValue(fieldsValue);
        }
      } else if (fieldName === "paddingLeft") {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        if (fieldsValue && fieldsValue.position && fieldsValue.position.isPaddingLocked === true) {
          fieldsValue.position.paddingTop = value;
          fieldsValue.position.paddingRight = value;
          fieldsValue.position.paddingBottom = value;
          this.props.form.setFieldsValue(fieldsValue);
        }
      } else if (fieldName === "isMarginLocked") {
        if (value === true) {
          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.position) {
            fieldsValue.position.marginRight = fieldsValue.position.marginTop;
            fieldsValue.position.marginBottom = fieldsValue.position.marginTop;
            fieldsValue.position.marginLeft = fieldsValue.position.marginTop;
            this.props.form.setFieldsValue(fieldsValue);
            return this.props.form.submit();
          }
        } else if (value === false) {
          return this.props.form.submit();
        }
      } else if (fieldName === "isPaddingLocked") {
        if (value === true) {
          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.position) {
            fieldsValue.position.paddingTop = fieldsValue.position.paddingLeft;
            fieldsValue.position.paddingRight = fieldsValue.position.paddingLeft;
            fieldsValue.position.paddingBottom = fieldsValue.position.paddingLeft;
            this.props.form.setFieldsValue(fieldsValue);
            return this.props.form.submit();
          }
        } else if (value === false) {
          return this.props.form.submit();
        }
      }

      if (
        fieldName === "widthStyle" ||
        fieldName === "heightStyle" ||
        fieldName === "maxWidthStyle" ||
        fieldName === "maxHeightStyle" ||
        fieldName === "minWidthStyle" ||
        fieldName === "minHeightStyle"
      ) {
        this.props.form.submit();
        return;
      }

      if (fieldName === "positionIds") {
        return this.props.form.submit();
      }
    } else if (changedFields[0].name[0] === "text") {
      const fieldName = changedFields[0].name[1] as string;
      const { value } = changedFields[0];

      const typographyFields = ["fontSize", "letterSpacing", "lineHeight"];

      if (typographyFields.includes(fieldName) && value === "") {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        if (fieldsValue?.text) {
          fieldsValue.text[fieldName] = undefined;
          this.props.form.setFieldsValue(fieldsValue);
        }
      }

      if (fieldName === "fontStyle") {
        this.props.form.submit();
        return;
      }
      if (fieldName === "typoIds") {
        return this.props.form.submit();
      }
    } else if (changedFields[0].name[0] === "border") {
      const fieldName = changedFields[0].name[1] as string;
      const { value } = changedFields[0];

      const borderFields = [
        "borderLeftWidth",
        "borderBottomWidth",
        "borderRightWidth",
        "borderTopWidth",
        "borderTopLeftRadius",
        "borderTopRightRadius",
        "borderBottomRightRadius",
        "borderBottomLeftRadius"
      ];

      if (borderFields.includes(fieldName) && value === "") {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        if (fieldsValue?.border) {
          fieldsValue.border[fieldName] = undefined;
          this.props.form.setFieldsValue(fieldsValue);
        }
      }

      if (fieldName === "borderLeftWidth") {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        if (
          fieldsValue &&
          fieldsValue.border &&
          (fieldsValue.border.isBorderWidthLocked === true || fieldsValue.border.isBorderWidthLocked === undefined)
        ) {
          fieldsValue.border.borderTopWidth = value || undefined;
          fieldsValue.border.borderRightWidth = value || undefined;
          fieldsValue.border.borderBottomWidth = value || undefined;
          fieldsValue.border.borderLeftWidth = value || undefined;
          this.props.form.setFieldsValue(fieldsValue);
        }
      } else if (fieldName === "borderTopLeftRadius") {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        if (
          fieldsValue &&
          fieldsValue.border &&
          (fieldsValue.border.isBorderRadiusLocked === true || fieldsValue.border.isBorderRadiusLocked === undefined)
        ) {
          fieldsValue.border.borderTopRightRadius = value || undefined;
          fieldsValue.border.borderBottomRightRadius = value || undefined;
          fieldsValue.border.borderBottomLeftRadius = value || undefined;
          fieldsValue.border.borderTopLeftRadius = value || undefined;
          this.props.form.setFieldsValue(fieldsValue);
        }
      } else if (fieldName === "isBorderWidthLocked") {
        if (value === true) {
          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.border) {
            fieldsValue.border.borderTopWidth = fieldsValue.border.borderLeftWidth;
            fieldsValue.border.borderRightWidth = fieldsValue.border.borderLeftWidth;
            fieldsValue.border.borderBottomWidth = fieldsValue.border.borderLeftWidth;
            fieldsValue.border.borderLeftWidth = fieldsValue.border.borderLeftWidth;
            this.props.form.setFieldsValue(fieldsValue);
            return this.props.form.submit();
          }
        } else if (value === false) {
          return this.props.form.submit();
        }
      } else if (fieldName === "isBorderRadiusLocked") {
        if (value === true) {
          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.border) {
            fieldsValue.border.borderTopRightRadius = fieldsValue.border.borderTopLeftRadius;
            fieldsValue.border.borderBottomRightRadius = fieldsValue.border.borderTopLeftRadius;
            fieldsValue.border.borderBottomLeftRadius = fieldsValue.border.borderTopLeftRadius;
            this.props.form.setFieldsValue(fieldsValue);
            return this.props.form.submit();
          }
        } else if (value === false) {
          return this.props.form.submit();
        }
      }

      if (fieldName === "borderIds") {
        return this.props.form.submit();
      }
    } else if (changedFields[0].name[0] === "shadow") {
      if (changedFields[0].name[1] === "shadowId") {
        return this.props.form.submit();
      }
    } else if (changedFields[0].name[0] === "fill") {
      if (changedFields[0].name[1] === "fillIds") {
        return this.props.form.submit();
      }
    } else if (changedFields[0].name[0] === "effects") {
      if (changedFields[0].name[1] === "effectsIds") {
        return this.props.form.submit();
      }
    }
    this.scheduleFormSubmit();
  };

  getClassnameForActiveStatus = (a): string => {
    if (a.isActive) return "kuika_rotate90deg";
    return "kuika_rotate0deg";
  };

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  getItemName = () => {
    if (this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI) {
      return this.props?.sd?.sdName;
    }
    return this.getDiFriendlyNameNameByKuikaId();
  };

  getDiFriendlyNameNameByKuikaId = (): string => {
    const kuikaId = this.props.sd.selectedKuikaIds[0];
    let result = kuikaId;
    if (kuikaId && kuikaId.length > 0) {
      const di = this.props.sd.screenDesignDictionary[parseInt(kuikaId, 10)];
      if (di?.friendlyName && di.friendlyName.length > 0) {
        result = di.friendlyName;
      }
    }
    return result;
  };

  render() {
    const sd = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
    const isReportDesigner = this.props.sd.dashboardState === DashboardState.reportDesigner;
    const isWebScreen = this.props.sd.getIsWebScreen();
    // Shadow and Effects are hidden on a mobile screen whose apps cannot render them: neither the Android
    // (Kotlin) nor the iOS (Swift) generator emits a shadow or a transform at all, so offering the controls
    // there would let a designer author something inert. React Native does render both (RNM-0153), and the
    // generator is chosen PER PLATFORM (RNM-0112) — so the question is the project's generation type, not its
    // project type. Conservative when the answer is not yet known: `configurations` is fetched on project
    // load, and until it lands this reads false, i.e. exactly the behaviour these sections had before. Outside the
    // RN_BETA_ALLOWLIST gate a React Native configuration is generated as Native, so it counts as Native here.
    const generatesReactNative: boolean = projectGeneratesReactNative(this.props.sd.configurations);
    const isMobileOnlyNativeScreen: boolean =
      this.props.sd.project.projectType !== EProjectType.Desktop && !isWebScreen && !generatesReactNative;

    const hasLayoutChanges = this.hasAnyValueInSection("position");
    const hasTextChanges = this.hasAnyValueInSection("text");
    const hasFillChanges = this.hasAnyValueInSection("fill");
    const hasBorderChanges = this.hasAnyValueInSection("border");
    const hasShadowChanges = this.hasAnyValueInSection("shadow");
    const hasEffectsChanges = this.hasAnyValueInSection("effects");

    // KOA-9069: the style sections read their values imperatively (`form.getFieldValue`) during
    // their OWN render, and PureSdComponent blocks that render while props and state are equal —
    // which they are when only the form's write target changes. Switching between base and a
    // condition therefore left every control painting the PREVIOUS target's values until some
    // unrelated change forced a render; QA read that as two conditions trading their styles, and
    // as a red condition showing transparent. Keying the sections on the target remounts them so
    // each control re-derives from the store. Only ever changes on a deliberate target switch.
    // The Conditional Styles section is deliberately NOT keyed: it owns the selection and holds
    // the focused name input, which a remount would blur mid-typing.
    const styleTargetKey: string = this.state.selectedConditionalStyleId ?? "base";

    const items = [
      {
        key: "1",
        label: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Layout</span>
            {hasLayoutChanges && (
              <ResetChangesPopover
                onReset={() => {
                  this.resetSectionChanges("position");
                }}
                section="layout"
                tokenColor={this.state.isEditMode}
              />
            )}
          </div>
        ),
        children: <PositionComponent key={styleTargetKey} form={this.props.form} isEditMode={this.state.isEditMode} />,
        className:
          this.props.sd.getStylesVisibility(InvisibleStylePart.Layout) === Visibility.Hidden ? "k_display-none" : "",
        forceRender: true
      },
      {
        key: "2",
        label: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Text</span>
            {hasTextChanges && (
              <ResetChangesPopover
                onReset={() => {
                  this.resetSectionChanges("text");
                }}
                section="text"
                tokenColor={this.state.isEditMode}
              />
            )}
          </div>
        ),
        children: (
          <TypographyComponent key={styleTargetKey} form={this.props.form} isEditMode={this.state.isEditMode} />
        ),
        className:
          this.props.sd.getStylesVisibility(InvisibleStylePart.Text) === Visibility.Hidden || selectedDi?.id === -1
            ? "k_display-none"
            : "",
        forceRender: true
      },
      {
        key: "3",
        label: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Fill</span>
            {hasFillChanges && (
              <ResetChangesPopover
                onReset={() => {
                  this.resetSectionChanges("fill");
                }}
                section="fill"
                tokenColor={this.state.isEditMode}
              />
            )}
          </div>
        ),
        children: <FillComponent key={styleTargetKey} form={this.props.form} isEditMode={this.state.isEditMode} />,
        className:
          this.props.sd.getStylesVisibility(InvisibleStylePart.Fill) === Visibility.Hidden ? "k_display-none" : "",
        forceRender: true
      },
      {
        key: "4",
        label: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Border</span>
            {hasBorderChanges && (
              <ResetChangesPopover
                onReset={() => {
                  this.resetSectionChanges("border");
                }}
                section="border"
                tokenColor={this.state.isEditMode}
              />
            )}
          </div>
        ),
        children: <BorderComponent key={styleTargetKey} form={this.props.form} isEditMode={this.state.isEditMode} />,
        className:
          this.props.sd.getStylesVisibility(InvisibleStylePart.Border) === Visibility.Hidden ? "k_display-none" : "",
        forceRender: true
      },
      {
        key: "5",
        label: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Shadow</span>
            {hasShadowChanges && (
              <ResetChangesPopover
                onReset={() => {
                  this.resetSectionChanges("shadow");
                }}
                section="shadow"
                tokenColor={this.state.isEditMode}
              />
            )}
          </div>
        ),
        children: <ShadowComponent key={styleTargetKey} form={this.props.form} isEditMode={this.state.isEditMode} />,
        className:
          this.props.sd.getStylesVisibility(InvisibleStylePart.Shadow) === Visibility.Hidden ||
          isMobileOnlyNativeScreen ||
          (isReportDesigner && selectedDi?.id === -1)
            ? "k_display-none"
            : "",
        forceRender: true
      },
      {
        key: "6",
        label: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span data-testid="styling-panel-section-effects">Effects</span>
            {hasEffectsChanges && (
              <ResetChangesPopover
                onReset={() => {
                  this.resetSectionChanges("effects");
                }}
                section="effects"
              />
            )}
          </div>
        ),
        children: (
          <EffectsComponent
            key={styleTargetKey}
            form={this.props.form}
            onRequestStateChange={this.handleStateChange}
            onPrepareStatePresetCommit={this.markStatePresetCommit}
          />
        ),
        className:
          this.props.sd.getStylesVisibility(InvisibleStylePart.Effects) === Visibility.Hidden ||
          isReportDesigner ||
          isMobileOnlyNativeScreen
            ? "k_display-none"
            : "",
        forceRender: true
      },
      {
        // KOA-9069. Web-only, like the state-CSS machinery it rides on: the conditional
        // block is emitted by the React generator and has no Android/iOS counterpart.
        key: "7",
        label: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span data-testid="styling-panel-section-conditional">Conditional Styles</span>
          </div>
        ),
        children: (
          <ConditionalStylingComponent
            selectedConditionalStyleId={this.state.selectedConditionalStyleId}
            onConditionalStylesChanged={this.handleConditionalStylesChanged}
            onSelectedConditionalStyleChanged={this.handleSelectedConditionalStyleChanged}
          />
        ),
        // Same web-only gate the Effects section uses: a Desktop project is web, so keying on
        // `isWebScreen` alone hid the section on every Desktop-project screen.
        className:
          isReportDesigner || (this.props.sd.project.projectType !== EProjectType.Desktop && !isWebScreen)
            ? "k_display-none"
            : "",
        forceRender: true
      }
    ];

    return (
      <>
        {this.props.sd.getVisibility(ViewPart.StylingPanel) === Visibility.Visible && (
          <>
            <Form
              form={this.props.form}
              name="basic"
              onFinish={this.handleStyleFormFinish}
              onFieldsChange={this.handleStyleFormFieldsChange}
            >
              <Form.Item name="styleGroupId" noStyle>
                <input type="hidden" />
              </Form.Item>
              <Form.Item name="styleGroupName" noStyle>
                <input type="hidden" />
              </Form.Item>
              <div
                className={classNames("kuika-styling-panel", {
                  "kuika-styling-panel--conditional-active": this.state.selectedConditionalStyleId !== null,
                  "kuika-styling-panel--state-active": this.state.selectedState !== "none",
                  "kuika-styling-panel--editing":
                    this.state.isEditMode &&
                    this.state.editingGroupId === "unified" &&
                    this.state.selectedState === "none"
                })}
              >
                <UnifiedStylingSelector
                  form={this.props.form}
                  onEditModeChange={this.handleEditModeChange}
                  editingGroupId={this.state.editingGroupId}
                  selectedState={this.state.selectedState}
                  onStateChange={this.handleStateChange}
                  onStateReset={this.handleStateReset}
                  onResetState={this.handleResetState}
                  pendingTagStateStyles={this.state.pendingTagStateStyles}
                  statePreviewBackup={this.state.statePreviewBackup}
                />
                {/* KOA-9069: while a condition is selected every section below writes into it,
                    not into the base style. Say so at the top, where it is visible without
                    scrolling down to the Conditional Styles section. */}
                {this.state.selectedConditionalStyleId !== null && (
                  <div className="kuika-conditional-styling__banner">
                    <KuikaSmallText>
                      {`Editing ${this.getSelectedConditionalStyleLabel()} — style changes below apply only when it holds.`}
                    </KuikaSmallText>
                    <KuikaButton type="text" size="sm" onClick={() => this.handleSelectedConditionalStyleChanged(null)}>
                      Done
                    </KuikaButton>
                  </div>
                )}
                <div
                  className={classNames("kuika-styling-panel__panels-wrapper", {
                    "kuika-styling-panel__panels-wrapper--editing":
                      this.state.isEditMode &&
                      this.state.editingGroupId === "unified" &&
                      this.state.selectedState === "none",
                    "kuika-styling-panel__panels-wrapper--editing-position":
                      this.state.isEditMode &&
                      this.state.editingGroupId === "layout" &&
                      this.state.selectedState === "none",
                    "kuika-styling-panel__panels-wrapper--editing-text":
                      this.state.isEditMode &&
                      this.state.editingGroupId === "text" &&
                      this.state.selectedState === "none",
                    "kuika-styling-panel__panels-wrapper--editing-fill":
                      this.state.isEditMode &&
                      this.state.editingGroupId === "fill" &&
                      this.state.selectedState === "none",
                    "kuika-styling-panel__panels-wrapper--editing-border":
                      this.state.isEditMode &&
                      this.state.editingGroupId === "border" &&
                      this.state.selectedState === "none",
                    "kuika-styling-panel__panels-wrapper--editing-shadow":
                      this.state.isEditMode &&
                      this.state.editingGroupId === "shadow" &&
                      this.state.selectedState === "none",
                    "kuika-styling-panel__panels-wrapper--editing-effects":
                      this.state.isEditMode &&
                      this.state.editingGroupId === "effects" &&
                      this.state.selectedState === "none",
                    "kuika-styling-panel__panels-wrapper--state-active": this.state.selectedState !== "none",
                    "kuika-styling-panel__panels-wrapper--conditional-active":
                      this.state.selectedConditionalStyleId !== null
                  })}
                >
                  <KuikaCollapsePanel
                    items={items}
                    onChange={this.collapseChanged}
                    activeKey={this.props.sd.stylingActiveKeys}
                    expandIconPosition="right"
                    forceRender
                  />
                  <RightPanelScrollEnd />
                </div>
              </div>
            </Form>
          </>
        )}
      </>
    );
  }

  /**
   * KOA-15838: does the selected DI actually reference the tag being edited?
   *
   * The seed-from-source edit-preview path (below) turns the selected DI into a
   * live preview of the tag by replacing its base with the tag's project entry.
   * That is correct when the DI already references the tag (editing an existing
   * tag in a state). But for a tag the DI does NOT reference — e.g. a tag the
   * user just created from a state, which the create flow never linked onto the
   * DI — replacing the DI base with the (often empty) fresh tag entry blanks the
   * component on the canvas. In that case we must preserve the DI's own cascade
   * instead (the none-mode editUnifiedStyle behaviour), which is what passing
   * seedBackupFromSource=false achieves.
   *
   * Read the DI's persisted styleObj (NOT the form — the create flow may have
   * already pushed the new tag's id into the form even though the DI itself was
   * never relinked). Match the DI's per-section *Ids against the tag's section
   * ids: for a unified tag those live across the 5 collections keyed by groupId;
   * for a legacy tag `tagId` IS the section id.
   */
  private selectedDiReferencesTag = (styleObj: IKuikaStyle | undefined, tagId: string | undefined): boolean => {
    if (!styleObj || !tagId) return false;
    const tagLower: string = tagId.toLowerCase();
    const project = this.props.sd.project;
    const idSet: Set<string> = new Set<string>([tagLower]);
    const addId = (id: string | null | undefined): void => {
      if (id) idSet.add(id.toLowerCase());
    };
    addId(project.stylingPosition?.find((s) => s.groupId?.toLowerCase() === tagLower)?.positionId);
    addId(project.stylingTypography?.find((s) => s.groupId?.toLowerCase() === tagLower)?.typoId);
    addId(project.stylingFill?.find((s) => s.groupId?.toLowerCase() === tagLower)?.fillId);
    addId(project.stylingBorder?.find((s) => s.groupId?.toLowerCase() === tagLower)?.borderId);
    addId(project.stylingShadow?.find((s) => s.groupId?.toLowerCase() === tagLower)?.shadowId);
    addId(project.stylingEffects?.find((s) => s.groupId?.toLowerCase() === tagLower)?.effectsId);

    const refsAny = (ids: (string | null)[] | null | undefined): boolean =>
      !!ids && ids.some((id) => id != null && idSet.has(id.toLowerCase()));
    return (
      refsAny(styleObj.position?.positionIds) ||
      refsAny(styleObj.text?.typoIds) ||
      refsAny(styleObj.fill?.fillIds) ||
      refsAny(styleObj.border?.borderIds) ||
      refsAny(styleObj.shadow?.shadowIds) ||
      refsAny(styleObj.effects?.effectsIds)
    );
  };

  handleEditModeChange = (isEditing: boolean, groupId: string | null, tagId?: string) => {
    if (isEditing) {
      // KOA-15838: snapshot the selected DI's styleObj BEFORE tag-edit begins.
      // During edit, handleStyleFormFinish mirrors the tag's state + base onto
      // this DI for the live canvas preview (the "Update DI for visual preview"
      // write). Tag-edit exit (save OR cancel) used to clear only isEditMode /
      // pendingTagStateStyles and never restore the DI, so that preview froze as
      // permanent inline residue — a leaked hover/focused/… equal to the tag,
      // surfacing as a false orange "locally changed" dot on every element that
      // references the tag. Entering edit from a non-none state means the DI's
      // base is already preview-mirrored, so rebuild a CLEAN base from the
      // statePreviewBackup before snapshotting.
      const enteringState: StyleState = this.state.selectedState;
      const kuikaId: string = this.props.sd.selectedKuikaIds[0];
      const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
      let editDiBackup: IEditDiBackup | null = null;
      if (selectedDi?.styleObj && kuikaId !== undefined) {
        const cleanStyleObj: IKuikaStyle = this.state.statePreviewBackup
          ? buildRestoredStyleObj(selectedDi.styleObj, this.state.statePreviewBackup)
          : (_.cloneDeep(selectedDi.styleObj) as IKuikaStyle);
        editDiBackup = { kuikaId, styleObj: cleanStyleObj };
      }

      // KOA-15838: edit-mode entered while a state tab (hover/pressed/focused/
      // disabled) is already active. editUnifiedStyle only knows how to load the
      // tag's BASE props, so without this the active state kept showing the tag's
      // base instead of its {state}Styles ("tag's hover styles didn't load" bug).
      // Once editingGroupId is committed (setState callback), re-source the form
      // from the tag's project entries for the active state. `seedBackupFromSource`
      // makes handleStateChange seed statePreviewBackup from the tag entries
      // directly (not the DI), so the preview base reflects the tag without
      // depending on an async DI write landing first — matching the already-correct
      // reverse ordering (enter edit in "none", then switch to the state).
      const enteringFromState: boolean = enteringState !== "none" && !!tagId;
      // KOA-15838 create-tag-in-state wipe fix: only seed the preview backup from
      // the tag (which replaces the DI base with the tag entry) when the DI
      // actually references the tag. For a freshly-created / unreferenced tag,
      // seeding from it blanks the DI; pass false so handleStateChange keeps the
      // DI's own clean base (cascade preserved), matching none-mode editing.
      const diReferencesTag: boolean = this.selectedDiReferencesTag(selectedDi?.styleObj, tagId);
      this.setState(
        {
          isEditMode: true,
          editingGroupId: groupId,
          editingTagId: tagId ?? null,
          pendingTagStateStyles: null,
          editDiBackup
        },
        () => {
          if (!enteringFromState || !tagId) return;
          // suppressPendingFlushOnce stops handleStateChange (previousState ===
          // enteringState here) from capturing the current (stale state-mode) form
          // into pendingTagStateStyles — that garbage would otherwise win over the
          // tag's real {state}Styles in the loadState pick.
          this.suppressPendingFlushOnce = true;
          this.handleStateChange(enteringState, tagId, diReferencesTag);
        }
      );
      return;
    }

    // Exiting tag-edit: restore the previewed DI so the edit leaves NO inline
    // residue — the DI keeps only its tag reference and inherits the (now-saved)
    // tag's state via the backend merge. This is the documented restore the
    // KOA-15838 follow-up TODO calls for.
    const { editDiBackup } = this.state;
    if (editDiBackup) {
      this.props.sd.applyPropertyGridChangesToDisplay(
        { propertyName: "styleObj", value: editDiBackup.styleObj, kuikaId: editDiBackup.kuikaId },
        ESdChangeReason.ChangeStyleValue
      );
    }
    this.setState({
      isEditMode: false,
      editingGroupId: null,
      editingTagId: null,
      pendingTagStateStyles: null,
      editDiBackup: null
    });
  };

  handleStateReset = () => {
    this.setState({ selectedState: "none", statePreviewBackup: null });
  };

  private resetBaseStyles = async (): Promise<void> => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    const groupId = fieldsValue.styleGroupId;
    const { isEditMode } = this.state;
    const excludeFields = BASE_RESET_EXCLUDE_FIELDS;

    // Step 1: In edit mode with a tag being edited, clear base props from project metadata
    if (isEditMode && groupId) {
      const project = _.cloneDeep(this.props.sd.project);
      const collectionNames: [keyof IKuikaStyle, string][] = [
        ["position", "stylingPosition"],
        ["text", "stylingTypography"],
        ["fill", "stylingFill"],
        ["border", "stylingBorder"],
        ["shadow", "stylingShadow"],
        ["effects", "stylingEffects"]
      ];

      collectionNames.forEach(([section, collectionName]) => {
        const collection = (project as any)[collectionName];
        if (!collection) return;
        const entry = collection.find((s: any) => s.groupId === groupId);
        if (!entry) return;
        const keep = new Set<string>([
          "id",
          "groupId",
          "name",
          "hoverStyles",
          "pressedStyles",
          "focusedStyles",
          "disabledStyles",
          ...(excludeFields[section as string] || [])
        ]);
        Object.keys(entry).forEach((key) => {
          if (!keep.has(key)) delete entry[key];
        });
      });

      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
    }

    // Step 2: Reset base props in either the form (if we're in "none") or the DI styleObj
    if (this.state.selectedState === "none") {
      // Form is currently showing base values — clear them and let submit propagate to DI
      const formValues = this.props.form.getFieldsValue();
      const resetValues = _.cloneDeep(formValues);
      STYLE_SECTIONS.forEach((section) => {
        const sectionData = (resetValues as any)[section];
        if (!sectionData) return;
        const exclude = excludeFields[section as string] || [];
        Object.keys(sectionData).forEach((key) => {
          if (!exclude.includes(key)) sectionData[key] = undefined;
        });
      });
      this.isStateTransitioning = true;
      this.props.form.resetFields();
      this.props.form.setFieldsValue(resetValues);
      this.isStateTransitioning = false;
      this.props.form.submit();
    } else {
      // Form shows state values; update DI.styleObj directly so base props are cleared while
      // hover/pressed/focused/disabled styles and ID references stay intact.
      const sd = this.props.sd.getScreenDesign();
      const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
      if (!selectedDi?.styleObj) return;

      const updatedStyleObj = _.cloneDeep(selectedDi.styleObj) as IKuikaStyle;
      const clearBaseInStyleObj = (target: IKuikaStyle): void => {
        STYLE_SECTIONS.forEach((section) => {
          const sectionData = (target as any)[section];
          if (!sectionData) return;
          const keep = new Set<string>([
            "hoverStyles",
            "pressedStyles",
            "focusedStyles",
            "disabledStyles",
            "positionId",
            "typoId",
            "fillId",
            "borderId",
            "shadowId",
            "effectsId",
            ...(excludeFields[section as string] || [])
          ]);
          Object.keys(sectionData).forEach((key) => {
            if (!keep.has(key)) delete sectionData[key];
          });
        });
      };

      clearBaseInStyleObj(updatedStyleObj);

      // Also clear base props from statePreviewBackup so re-switching to "none" reflects the reset
      let updatedBackup = this.state.statePreviewBackup;
      if (updatedBackup) {
        updatedBackup = _.cloneDeep(updatedBackup);
        clearBaseInStyleObj(updatedBackup);
      }

      const kuikaId = this.props.sd.selectedKuikaIds[0];
      this.props.sd.applyPropertyGridChangesToDisplay(
        { propertyName: "styleObj", value: updatedStyleObj, kuikaId },
        ESdChangeReason.ChangeStyleValue
      );

      if (updatedBackup !== this.state.statePreviewBackup) {
        this.setState({ statePreviewBackup: updatedBackup });
      }
    }
  };

  handleResetState = async (state: StyleState) => {
    if (state === "none") {
      await this.resetBaseStyles();
      return;
    }

    const stateProp = `${state}Styles`;
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    const groupId = fieldsValue.styleGroupId;
    const { isEditMode, editingGroupId } = this.state;

    if (isEditMode && groupId) {
      // Editing a style tag: clear state styles from project metadata
      const project = _.cloneDeep(this.props.sd.project);

      if (editingGroupId === "unified") {
        // Unified tag: every collection shares one `groupId`.
        const collections: string[] = [
          "stylingPosition",
          "stylingTypography",
          "stylingFill",
          "stylingBorder",
          "stylingShadow",
          "stylingEffects"
        ];
        collections.forEach((collectionName) => {
          const collection = (project as any)[collectionName];
          if (!collection) return;
          const entry = collection.find((s: any) => s.groupId === groupId);
          if (entry) {
            delete entry[stateProp];
          }
        });
      } else {
        // KOA-15838 legacy-reset fix: a single-panel (legacy) tag has NO
        // `groupId` — its entry is keyed by the section id (`fillId`/`typoId`/
        // …), which `form.styleGroupId` (=`groupId` here) carries while a state
        // is active. Matching on `s.groupId === groupId` finds nothing, so the
        // reset's `delete entry[stateProp]` no-ops and the cleared state comes
        // back on the next save. Resolve the edited collection from
        // `editingGroupId` and match by its own id field, mirroring
        // saveUnifiedStyle's legacy persist path.
        const idLower: string = groupId.toLowerCase();
        const legacyMap: Record<string, [string, string]> = {
          layout: ["stylingPosition", "positionId"],
          text: ["stylingTypography", "typoId"],
          fill: ["stylingFill", "fillId"],
          border: ["stylingBorder", "borderId"],
          shadow: ["stylingShadow", "shadowId"],
          effects: ["stylingEffects", "effectsId"]
        };
        const mapping: [string, string] | undefined = editingGroupId ? legacyMap[editingGroupId] : undefined;
        if (mapping) {
          const [collectionName, idField] = mapping;
          const collection = (project as any)[collectionName];
          const entry = collection?.find((s: any) => s[idField]?.toLowerCase() === idLower);
          if (entry) {
            delete entry[stateProp];
          }
        }
      }

      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

      // Clear the corresponding state from pendingTagStateStyles
      const { pendingTagStateStyles } = this.state;
      let updatedPending = pendingTagStateStyles;
      if (updatedPending) {
        updatedPending = _.cloneDeep(updatedPending);
        STYLE_SECTIONS.forEach((section) => {
          const sectionData = (updatedPending as any)?.[section];
          if (sectionData?.[stateProp]) {
            delete sectionData[stateProp];
          }
        });
      }

      // KOA-15838 reset-stays-in-state (edit-mode parity with the direct branch
      // below): resetting the active state's overlay used to drop the panel back
      // to "none". The user reset a state — that is no reason to leave the state.
      // Stay in the same state and reload the form from the now-cleared project
      // entry + pending so the form shows an empty state sub-object. Pass
      // `groupId` (= fieldsValue.styleGroupId, which carries the unified groupId
      // or the legacy section id) so handleStateChange re-sources from the tag's
      // project entries rather than the DI.
      if (this.state.selectedState === state) {
        this.setState({ pendingTagStateStyles: updatedPending }, () => {
          // Suppress handleStateChange's outgoing-state flush: the form still
          // holds the just-reset values and re-capturing them into pending
          // would undo this reset on the next saveUnifiedStyle.
          this.suppressPendingFlushOnce = true;
          this.handleStateChange(state, groupId);
        });
      } else {
        this.setState({ pendingTagStateStyles: updatedPending });
      }
    } else {
      // Not editing a style tag: clear state styles from the element's styleObj
      const sd = this.props.sd.getScreenDesign();
      const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
      if (!selectedDi?.styleObj) return;

      const { statePreviewBackup } = this.state;
      const isResettingActiveState = this.state.selectedState === state;

      // KOA-15838 reset-leak fix: while in state mode, selectedDi.styleObj base
      // alanları `saveFormDataToStateStyles(..., updateBaseForPreview=true)`
      // tarafından state'in preview değerleri ile yazılmış durumda. Sadece
      // [stateProp]'u silmek base'deki preview artıklarını bırakır ve bir
      // sonraki "none" exit / persist sırasında bu kirli base kalıcı hale
      // gelir ("style'ları none'a taşıdı" hatası). Aktif state reset edilirken
      // base'i statePreviewBackup'tan restore et, ardından [stateProp]'u sil.
      let updatedStyleObj: IKuikaStyle;
      if (isResettingActiveState && statePreviewBackup) {
        updatedStyleObj = buildRestoredStyleObj(selectedDi.styleObj, statePreviewBackup, !this.state.isEditMode);
      } else {
        updatedStyleObj = _.cloneDeep(selectedDi.styleObj) as IKuikaStyle;
      }

      STYLE_SECTIONS.forEach((section) => {
        const sectionData = (updatedStyleObj as any)[section];
        if (sectionData?.[stateProp]) {
          delete sectionData[stateProp];
        }
      });

      const kuikaId = this.props.sd.selectedKuikaIds[0];
      this.props.sd.applyPropertyGridChangesToDisplay(
        { propertyName: "styleObj", value: updatedStyleObj, kuikaId },
        ESdChangeReason.ChangeStyleValue
      );

      // KOA-15838 reset-stays-in-state: önceden reset, etkin state'e eşitse
      // panel'i otomatik olarak "none"a düşürüyordu. Kullanıcı reset yaptı
      // diye state'ten çıkmak zorunda değil — aynı state'te kal, form'u
      // temizlenmiş (boş) state sub-objesi ile yeniden yükle. handleStateChange
      // çağrısı backup'ı korur ve form fields'i temiz srcSection'dan
      // tazeler.
      if (isResettingActiveState) {
        this.handleStateChange(state);
      }
    }
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    const action: StyleFormSyncAction = resolveStyleFormSync(
      changeReason,
      this.state.selectedState,
      this.state.isEditMode,
      this.state.selectedConditionalStyleId !== null
    );
    if (action === "ignore") {
      return false;
    }
    // See resolveStyleFormSync: a write from outside the panel must not be overwritten by the panel's
    // own pending debounced submit, which still carries the pre-write values. The selection bar flushes
    // that submit BEFORE it writes (`stylingPanelModeStore.flushPendingEdit`), so nothing is pending by
    // now on its writes; this cancel is the safety net for any other outside writer.
    if (action === "dropPending" || action === "dropPendingAndReload") {
      this.cancelPendingSubmit();
    }
    if (action === "reload" || action === "dropPendingAndReload") {
      this.loadStyleForm();
    }
    return true;
  };
}

const stylingWindow = WithForm<IStylingWindowProps>(withSD<IStylingWindowProps>(StylingWindow));
export { stylingWindow as StylingWindow };
/** The unwrapped class, for lifecycle tests that cannot mount the whole panel. */
export { StylingWindow as StylingWindowView };
